import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "./useAuth";

const EMPTY = {
  workflow: { profession: "", customRole: "", audit: null, extra: [], search: "", vision: "", custom: null },
  toolStack: { building: "", input: "", result: null },
  toolRec: { goal: "", input: "", result: null },
  competitor: { idea: "", result: null },
  nameChecker: { name: "", result: null },
  scanner: { image: null, buildType: "", goal: "", audience: "", result: null, phase: "capture" },
  market: {
    idea: "",
    inputs: { totalMarket: 1000000, penetration: 5, price: 30, conversion: 20, growth: 30 },
    insights: null,
  },
  validation: {
    form: { name: "", does: "", customer: "", problem: "", different: "" },
    report: null,
    history: [],
    roadmap: null,
    roadmapDone: [],
  },
};

const clone = (o) => JSON.parse(JSON.stringify(o));

const AIResourcesContext = createContext(null);

export const AIResourcesProvider = ({ children }) => {
  const { user } = useAuth();
  const [data, setData] = useState(() => clone(EMPTY));

  // Only reset on sign-out. Work persists across navigation so founders don't
  // lose results (and burn a search try) by leaving the AI Resources section.
  // Re-running/redoing a tool overwrites that tool's own result.
  const hadUser = useRef(Boolean(user));
  useEffect(() => {
    if (hadUser.current && !user) setData(clone(EMPTY));
    hadUser.current = Boolean(user);
  }, [user]);

  const patchWorkflow = useCallback((p) => setData((d) => ({ ...d, workflow: { ...d.workflow, ...p } })), []);
  const patchToolStack = useCallback((p) => setData((d) => ({ ...d, toolStack: { ...d.toolStack, ...p } })), []);
  const patchToolRec = useCallback((p) => setData((d) => ({ ...d, toolRec: { ...d.toolRec, ...p } })), []);
  const patchCompetitor = useCallback((p) => setData((d) => ({ ...d, competitor: { ...d.competitor, ...p } })), []);
  const patchNameChecker = useCallback((p) => setData((d) => ({ ...d, nameChecker: { ...d.nameChecker, ...p } })), []);
  const patchScanner = useCallback((p) => setData((d) => ({ ...d, scanner: { ...d.scanner, ...p } })), []);
  const patchMarket = useCallback((p) => setData((d) => ({ ...d, market: { ...d.market, ...p } })), []);
  const patchValidation = useCallback((p) => setData((d) => ({ ...d, validation: { ...d.validation, ...p } })), []);

  return (
    <AIResourcesContext.Provider
      value={{
        workflow: data.workflow, patchWorkflow,
        toolStack: data.toolStack, patchToolStack,
        toolRec: data.toolRec, patchToolRec,
        competitor: data.competitor, patchCompetitor,
        nameChecker: data.nameChecker, patchNameChecker,
        scanner: data.scanner, patchScanner,
        market: data.market, patchMarket,
        validation: data.validation, patchValidation,
      }}
    >
      {children}
    </AIResourcesContext.Provider>
  );
};

export const useAIResources = () => useContext(AIResourcesContext);
